<template>
  <div v-if="currentDonation" class="review-payment">
    <h2 v-translate>Review details and confirm</h2>

    <div class="summary-container">
      <!-- Anonymous Donor fields-->
      <div v-if="currentAnonymousDonor && currentAnonymousDonor.firstName" class="summary-item">
        <h5 v-translate>First name</h5>
        <p class="summary-item-value">{{ currentAnonymousDonor.firstName }}</p>
      </div>

      <div v-if="currentAnonymousDonor && currentAnonymousDonor.lastName" class="summary-item">
        <h5 v-translate>Last name</h5>
        <p class="summary-item-value">{{ currentAnonymousDonor.lastName }}</p>
      </div>

      <div v-if="currentAnonymousDonor && currentAnonymousDonor.email" class="summary-item">
        <h5 v-translate>Email</h5>
        <p class="summary-item-value">{{ currentAnonymousDonor.email }}</p>
      </div>

      <div
        v-if="currentAnonymousDonor && currentAnonymousDonor.orgRelationship"
        class="summary-item"
      >
        <h5 v-translate>
          Your relationship to %{organizationDetails.name}
        </h5>
        <p class="summary-item-value">{{ $gettext(formattedOrgRelationship) }}</p>
      </div>

      <div v-if="currentAnonymousDonor && currentAnonymousDonor.employeeId" class="summary-item">
        <h5 v-translate>Unique ID</h5>
        <p class="summary-item-value">{{ currentAnonymousDonor.employeeId }}</p>
      </div>

      <div
        v-if="currentAnonymousDonor && currentAnonymousDonor.otherRelationship"
        class="summary-item"
      >
        <h5 v-translate>Details</h5>
        <p class="summary-item-value">{{ currentAnonymousDonor.otherRelationship }}</p>
      </div>

      <div v-if="currentAnonymousDonor && currentAnonymousDonor.zipCode" class="summary-item">
        <h5 v-translate>Zip code</h5>
        <p class="summary-item-value">{{ currentAnonymousDonor.zipCode }}</p>
      </div>
      <!-- End Anonymous Donor fields -->

      <div class="summary-item">
        <h5 v-translate>Amount to give</h5>
        <p class="summary-item-value">{{ currentDonation.amount | currency }}</p>
      </div>
      <!-- Anonymous donations may not have a payment source display populated -->
      <div v-if="currentDonation.paymentLast4" class="summary-item">
        <h5 v-translate>Payment method</h5>
        <p class="summary-item-value" v-translate>{{ $gettext(currentDonation.paymentType) }} {{ currentDonation.paymentLast4 }}</p>
      </div>
      <div class="summary-item">
        <h5 v-translate>Frequency</h5>
        <p class="summary-item-value">{{ $gettext(formattedFrequency) }}</p>
      </div>
    </div>

    <LoadingSpinnerButton
      class="button"
      :text="$gettext('Give')"
      :loading="loading"
      @click="onDonate"
    />
    <md-button>
      <p class="back-button" @click="onClickBack" v-translate>Edit</p>
    </md-button>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'

import { analyticsMixin } from '@/services/googleAnalytics'
import Donation from '@/services/donations'
import AnonymousDonor from '@/services/anonymousDonors'

import LoadingSpinnerButton from '@/components/LoadingSpinnerButton'

export default {
  name: 'DonationReview',
  mixins: [analyticsMixin],
  components: {
    LoadingSpinnerButton,
  },
  data() {
    return {
      loading: false,
      stripeToken: null,
    }
  },
  created() {
    // If this page is inappropriately accessed without a donation, route Home.
    if (!this.currentDonation) {
      this.$router.push({ name: 'Home' })
    }
    // If this page is accessed by an anonymous donor, route back to the credit card
    // screen if they haven't included a Stripe token.
    if (!this.userIsLoggedIn) {
      if (!this.$route.params.token) {
        this.$router.push({ name: 'AddPayment' })
      } else {
        this.stripeToken = this.$route.params.token
      }
    }
  },
  methods: {
    onClickBack() {
      this.$router.push({ name: 'AddPayment', params: { donationType: 'donation' } })
    },
    /* Submit donation! */
    async onDonate() {
      this.loading = true
      try {
        if (this.userIsLoggedIn) {
          if (this.currentDonation.frequency === Donation.options.RECURRING) {
            await this.donateMonthly()
          } else {
            await this.donateOneTime()
          }
        } else {
          if (this.currentDonation.frequency === Donation.options.RECURRING) {
            await this.donateMonthlyAnonymous()
          } else {
            await this.donateOneTimeAnonymous()
          }
        }
      } finally {
        this.loading = false
      }
    },
    /**
     * Perform a one-time donation for an authenticated user.
     **/
    async donateOneTime() {
      this.trackClickedCTA('give now')
      return Donation.api.submitDonation(this.currentDonation).then(result => {
        // Clear our current donation from state, since it's been processed
        this.$store.dispatch('clearCurrentDonation')
        // Route to summary screen
        this.$router.push({ name: 'DonationSummary', params: { id: result.id } })
      })
    },
    /**
     * Set up a monthly recurring donation for an authenticated user.
     **/
    async donateMonthly() {
      this.trackClickedCTA('give now')
      return Donation.api.startRecurringDonation(this.currentDonation.amount).then(employee => {
        // Update current user to update "isMonthlyDonor" flag
        this.$store.dispatch('updateUser', employee)

        // Now, we need to pass donation info to the Donation Summary screen.
        //
        // However, the donation isn't actually created in the database until we
        // receive a webhook from Stripe. So instead we pass in the donation object
        // from state, updating it so that it has all the required fields.
        this.currentDonation.datetimeCreated = new Date()
        this.currentDonation.isSubscriptionActive = true
        this.$router.push({ name: 'DonationSummary' })
      })
    },
    /**
     * Create an anonymous donor AND perform a one-time donation.
     **/
    async donateOneTimeAnonymous() {
      this.trackClickedCTA('give now')
      const donor = await AnonymousDonor.api.create(this.currentAnonymousDonor, this.stripeToken)
      const params = { donorId: donor.id, magicToken: donor.magicToken }
      return Donation.api.submitDonation(this.currentDonation, params).then(result => {
        // Clear out relevant info from state, since it's been processed
        this.$store.dispatch('clearCurrentDonation')
        this.$store.dispatch('clearCurrentAnonymousDonor')
        // Route to summary screen
        this.$router.push({ name: 'DonationSummary', params: { id: result.id }, query: params })
      })
    },
    /**
     * Create an anonymous donor AND set up recurring anonymous donation.
     **/
    async donateMonthlyAnonymous() {
      this.trackClickedCTA('give now')
      const donor = await AnonymousDonor.api.create(this.currentAnonymousDonor, this.stripeToken)
      const params = { donorId: donor.id, magicToken: donor.magicToken }
      const donation = await AnonymousDonor.api.startRecurringDonation(
        this.currentDonation.amount,
        params,
      )
      // Clear out relevant info from state, since it's been processed
      this.$store.dispatch('clearCurrentAnonymousDonor')

      // Now, we need to pass donation info to the Donation Summary screen.
      //
      // However, the donation isn't actually created in the database until we
      // receive a webhook from Stripe. So instead we pass in the donation object
      // from state, updating it so that it has all the required fields.
      this.currentDonation.datetimeCreated = new Date()
      this.currentDonation.isSubscriptionActive = true
      this.$router.push({
        name: 'DonationSummary',
        query: params,
      })
    },
  },
  computed: {
    ...mapState(['user', 'currentDonation', 'organizationDetails', 'currentAnonymousDonor']),
    ...mapGetters(['userIsLoggedIn']),
    formattedFrequency() {
      return Donation.options.FREQUENCY_CHOICES.find(
        choice => choice.value === this.currentDonation.frequency,
      ).labelEn
    },
    formattedOrgRelationship() {
      const label = AnonymousDonor.options.ORG_RELATIONSHIP_CHOICES.find(
        choice => choice.value === this.currentAnonymousDonor.orgRelationship,
      ).label
      // Use organization custom variable if it exists
      if (this.currentAnonymousDonor.orgRelationship === AnonymousDonor.options.EMPLOYEE) {
        return this.organizationDetails.customVariables.cv3 || label
      } else {
        return label
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/styles/variables';
@import '@/styles/mixins/buttons';
@import '@/styles/mixins/containers';
.review-payment {
  @include screen-container();
  justify-content: flex-start;
}
.button {
  margin-top: 2rem;
}
.summary-container {
  width: 100%;
}
.summary-item {
  border-bottom: 1px solid $soft-gray;
  padding: 20px 0;
}
.summary-item-value {
  font-weight: 800;
}
h5 {
  color: $base-gray;
  margin-bottom: 0.5rem;
}
.back-button {
  color: $base-gray;
}
</style>
